import React, { JSX, useEffect, useRef, useState } from "react";

interface VerificationInputProps {
  length?: number;
  onChange?: (value: string) => void;
  loading?: boolean;
  defaultValue?: string;
}

interface StateType {
  [key: number]: string;
}

function VerificationInput({
  length = 5,
  onChange,
  loading = false,
  defaultValue,
}: VerificationInputProps): JSX.Element {
  const arr = Array.from({ length }, (_, i) => i + 1);
  const defaultArray = defaultValue?.split("");

  const [state, setState] = useState<StateType>(
    arr.reduce((acc: StateType, item) => {
      acc[item] = defaultArray ? defaultArray[item - 1] : "";
      return acc;
    }, {})
  );

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  const handleInputChange = (
    item: number,
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const updatedState = {
      ...state,
      [item]: e.target.value,
    };

    setState(updatedState);

    const currentIndex = arr.indexOf(item);

    if (
      e.target.value !== "" &&
      currentIndex < length - 1 &&
      inputRefs.current[currentIndex + 1]
    ) {
      const nextInput = inputRefs.current[currentIndex + 1];
      nextInput?.focus();
      nextInput?.select();
    }

    if (onChange) {
      if (Object.values(updatedState).join("").length === length) {
        onChange(Object.values(updatedState).join(""));
      }
    }
  };

  const handleInputKeyDown = (
    item: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ): void => {
    if (e.key === "Backspace" && e.currentTarget.value === "") {
      const currentIndex = arr.indexOf(item);
      if (currentIndex > 0 && inputRefs.current[currentIndex - 1]) {
        const previousInput = inputRefs.current[currentIndex - 1];
        previousInput?.focus();
        previousInput?.select();
      }
    }
  };

  return (
    <div className="grid grid-cols-5 gap-1 items-center w-full mx-auto">
      {arr.map((item, index) => (
        <input
          ref={(el) => {
            inputRefs.current[index] = el;
          }}
          id={`verification-input-${index + 1}`}
          value={state[item]}
          onChange={(e) => handleInputChange(item, e)}
          onFocus={(e) => e.target.select()}
          onKeyDown={(e) => handleInputKeyDown(item, e)}
          key={index}
          type="text"
          maxLength={1}
          minLength={1}
          pattern="[0-9]*"
          inputMode="numeric"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          required
          disabled={loading}
          className={`
            w-12 h-12 text-center text-2xl rounded-lg
            border border-slate-800 
            bg-slate-200
            text-slate-950
            focus:outline-hidden focus:ring-2 focus:slate-500
            disabled:bg-gray-100
            disabled:text-gray-400
            disabled:cursor-not-allowed
          `}
        />
      ))}
    </div>
  );
}

export default VerificationInput;
